import React from "react";
import { TrashIcon, UserCircleIcon } from "@heroicons/react/16/solid";
import { StarIcon } from "@heroicons/react/24/solid";
import { DocumentArrowDownIcon, EyeIcon } from "@heroicons/react/24/outline";
import Relationship from "../../../assets/icons/relationship_couple_with_heart.svg";
import Sleep from "../../../assets/icons/sleep_moon_bed_zzz.svg";
import Goals from "../../../assets/icons/gratitude_mountain_footsteps_flag.svg";
import Anxiety from "../../../assets/icons/habits_activities_chefs_hat.svg";
import Button from "../../../componentLibrary/Button";
import TopicButton from "../../../componentLibrary/TopicButton";
import PsychButton from "../../../componentLibrary/PsychButton";

export default function ButtonComponents() {
  return (
    <>
      <h2 className="underline">Button Components</h2>

      {/* Main Button Table - Full Width */}
      <div className="mb-8">
        <h3 className="mb-4">Button Variants</h3>
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            Where these are used:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <h5 className="font-medium mb-2">Primary Variant:</h5>
              <div className="mb-2">
                <Button variant="primary">Primary</Button>
              </div>
              <p>
                Main call-to-action buttons throughout the application -
                login/signup flows, form submissions, and primary user actions.
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-2">Secondary Variant:</h5>
              <div className="mb-2">
                <Button variant="secondary">Secondary</Button>
              </div>
              <p>
                Used for navigating backward (previous page) and alternative
                actions. Provides clear way to go back or cancel current
                actions.
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-2">Default Variant:</h5>
              <div className="mb-2">
                <Button variant="default">Default</Button>
              </div>
              <p>
                Rarely used - standard buttons for general interactions when
                other variants don&apos;t fit the specific use case.
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-2">Alternative Variant:</h5>
              <div className="mb-2">
                <Button variant="alternative">Alternative</Button>
              </div>
              <p>
                Used when a full button won&apos;t fit in the available space.
                Compact alternative for space-constrained interfaces.
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-2">Dark Variant:</h5>
              <div className="mb-2">
                <Button variant="dark">Dark</Button>
              </div>
              <p>
                Used for adding, cutting, and pasting operations. High contrast
                buttons for important actions on light backgrounds.
              </p>
            </div>
            <div>
              <h5 className="font-medium mb-2">Cautionary Variant:</h5>
              <div className="mb-2">
                <Button variant="cautionary">Cautionary</Button>
              </div>
              <p>
                Destructive or warning actions - delete buttons, remove actions,
                and operations that require user confirmation.
              </p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-2 px-4 font-medium text-gray-700">
                  Type
                </th>
                <th className="text-left py-2 px-4 font-medium text-gray-700">
                  Primary
                </th>
                <th className="text-left py-2 px-4 font-medium text-gray-700">
                  Secondary
                </th>
                <th className="text-left py-2 px-4 font-medium text-gray-700">
                  Default
                </th>
                <th className="text-left py-2 px-4 font-medium text-gray-700">
                  Alternative
                </th>
                <th className="text-left py-2 px-4 font-medium text-gray-700">
                  Dark
                </th>
                <th className="text-left py-2 px-4 font-medium text-gray-700">
                  Cautionary
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">Large</td>
                <td className="py-3 px-4">
                  <Button variant="primary" size="lg">
                    Primary
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="secondary" size="lg">
                    Secondary
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="default" size="lg">
                    Default
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="alternative" size="lg">
                    Alternative
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="dark" size="lg">
                    Dark
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="cautionary" size="lg">
                    Cautionary
                  </Button>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">Medium</td>
                <td className="py-3 px-4">
                  <Button variant="primary">Primary</Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="secondary">Secondary</Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="default">Default</Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="alternative">Alternative</Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="dark">Dark</Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="cautionary">Cautionary</Button>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">Small</td>
                <td className="py-3 px-4">
                  <Button variant="primary" size="sm">
                    <UserCircleIcon className="w-4 h-4" />
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="secondary" size="sm">
                    <UserCircleIcon className="w-4 h-4" />
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="default" size="sm">
                    <UserCircleIcon className="w-4 h-4" />
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="alternative" size="sm">
                    <UserCircleIcon className="w-4 h-4" />
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="dark" size="sm">
                    <UserCircleIcon className="w-4 h-4" />
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="cautionary" size="sm">
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">Rounded</td>
                <td className="py-3 px-4">
                  <Button variant="primary" roundedFull>
                    Log in
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="secondary" roundedFull>
                    Secondary
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="default" roundedFull>
                    Default
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="alternative" roundedFull>
                    Alternative
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="dark" roundedFull>
                    Dark
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="cautionary" roundedFull>
                    Cautionary
                  </Button>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">Square</td>
                <td className="py-3 px-4">
                  <Button.Square className="w-10 h-10" variant="primary">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Square>
                </td>
                <td className="py-3 px-4">
                  <Button.Square className="w-10 h-10" variant="secondary">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Square>
                </td>
                <td className="py-3 px-4">
                  <Button.Square className="w-10 h-10" variant="default">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Square>
                </td>
                <td className="py-3 px-4">
                  <Button.Square className="w-10 h-10" variant="alternative">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Square>
                </td>
                <td className="py-3 px-4">
                  <Button.Square className="w-10 h-10" variant="dark">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Square>
                </td>
                <td className="py-3 px-4">
                  <Button.Square className="w-10 h-10" variant="cautionary">
                    <TrashIcon className="w-5 h-5" />
                  </Button.Square>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">Circle</td>
                <td className="py-3 px-4">
                  <Button.Circle className="w-10 h-10" variant="primary">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Circle>
                </td>
                <td className="py-3 px-4">
                  <Button.Circle className="w-10 h-10" variant="secondary">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Circle>
                </td>
                <td className="py-3 px-4">
                  <Button.Circle className="w-10 h-10" variant="custom">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Circle>
                </td>
                <td className="py-3 px-4">
                  <Button.Circle className="w-10 h-10" variant="custom">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Circle>
                </td>
                <td className="py-3 px-4">
                  <Button.Circle className="w-10 h-10" variant="dark">
                    <UserCircleIcon className="w-5 h-5" />
                  </Button.Circle>
                </td>
                <td className="py-3 px-4">
                  <Button.Circle className="w-10 h-10" variant="cautionary">
                    <TrashIcon className="w-5 h-5" />
                  </Button.Circle>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">
                  Icon + text
                </td>
                <td className="py-3 px-4">
                  <Button variant="primary" size="xxs" layout="iconText">
                    <EyeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Preview</span>
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="secondary" size="xxs" layout="iconText">
                    <EyeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Preview</span>
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="default" size="xxs" layout="iconText">
                    <DocumentArrowDownIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Save</span>
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="alternative" size="xxs" layout="iconText">
                    <EyeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>View</span>
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="dark" size="xxs" layout="iconText">
                    <DocumentArrowDownIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Save</span>
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="cautionary" size="xxs" layout="iconText">
                    <TrashIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>Delete</span>
                  </Button>
                </td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium text-gray-600">
                  Disabled
                </td>
                <td className="py-3 px-4">
                  <Button variant="primary" disabled>
                    Disabled
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="secondary" disabled>
                    Disabled
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="default" disabled>
                    Disabled
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="alternative" disabled>
                    Disabled
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="dark" disabled>
                    Disabled
                  </Button>
                </td>
                <td className="py-3 px-4">
                  <Button variant="cautionary" disabled>
                    Disabled
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Specialized Button Components */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="mb-4">Module Buttons</h3>
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Usage:</h4>
            <p className="text-sm text-gray-700">
              Specialized navigation buttons for modules and lessons. Used in
              module pages, explore sections, and lesson linking components.
              Features custom styling with icons and color theming.
            </p>
          </div>
          <div className="space-y-2">
            <TopicButton
              icon={
                <img
                  className="w-12 h-12"
                  src={Relationship}
                  alt="A chefs hat, music notes, and dumbbell"
                />
              }
              text="Test"
              color="#FFEBEF"
            />
            <TopicButton
              icon={
                <img
                  className="w-12 h-12"
                  src={Sleep}
                  alt="A couple facing each other with a heart between them"
                />
              }
              text="Test"
              color="#FFEBEF"
              variant="tall"
            />
          </div>
        </div>
        <div>
          <h3 className="mb-4">Action Buttons (PsychButton)</h3>
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Usage:</h4>
            <p className="text-sm text-gray-700">
              Split buttons that combine main navigation with secondary actions
              like bookmarking or favoriting. Used throughout the application
              for module navigation, favorite lists, and draft management.
              Features both wide and tall layout variants.
            </p>
          </div>
          <div className="space-y-2">
            <PsychButton
              icon={
                <img
                  className="w-12 h-12"
                  src={Goals}
                  alt="A couple facing each other with a heart between them"
                />
              }
              text="Test"
              favButton={<StarIcon color="#38B2E0" className="h-6 w-6" />}
            />
            <PsychButton
              icon={
                <img
                  className="w-12 h-12"
                  src={Anxiety}
                  alt="A couple facing each other with a heart between them"
                />
              }
              text="Test"
              favButton={<StarIcon color="#38B2E0" className="h-6 w-6" />}
              variant="tall"
            />
          </div>
        </div>
      </div>
    </>
  );
}
